import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PlaceIcon from "@mui/icons-material/Place";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// Components
import NavbarTalent from "../talent/components/NavbarTalent";
import TalentSidebar from "./components/TalentSidebar";
import MediaGallery from "./components/MediaGallery";
import VideoReels from "./components/VideoReels";
import AudioReels from "./components/AudioReels";
import ResumeProjectsSection from "./components/ResumeProjectsSection";
import ResumeTextSections from "./components/ResumeTextSections";
import RightNav from "./components/RightNav";
import ContactDialog from "./components/ContactDialog";
import MessagePrompt from "../components/MessagePrompt";

import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";

const TalentPublicView = () => {
  const { username } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [resolvedId, setResolvedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [talentData, setTalentData] = useState({});
  const [affiliations, setAffiliations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vocalTypes, setVocalTypes] = useState([]);
  const [vocalRange, setVocalRange] = useState([]);
  const [media, setMedia] = useState([]);
  const [groupedProjects, setGroupedProjects] = useState([]);
  const [resumeSections, setResumeSections] = useState([]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);

  const [headings, setHeadings] = useState([]);
  const [representationInfoList, setRepresentationInfoList] = useState({});
  const visitorUserId = Number(localStorage.getItem("userId")) || null;
  const visitorEmail = localStorage.getItem("userEmail") || ""; // if you store it
  const isLoggedIn = !!visitorUserId;

  // STEP 1: Resolve username -> userId
  useEffect(() => {
    const resolveUserId = async () => {
      if (!username) return;
      try {
        const resp = await api.get(ENDPOINTS.getUserId({ userName: username }));
        const userId = resp?.data?.userId;
        if (userId && !isNaN(userId)) {
          setResolvedId(userId);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error("Error resolving username:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    resolveUserId();
  }, [username]);

  // STEP 2: Fetch all talent data
  useEffect(() => {
    if (!resolvedId || isNaN(resolvedId)) return;

    const loadAll = async () => {
      try {
        const [
          profileRes,
          affRes,
          locRes,
          vocalRes,
          mediaRes,
          projRes,
          textRes,
          headingsRes,
          vocalRangeRes,
        ] = await Promise.all([
          api.get(`api/TalentProfile/get-talent-profile/${resolvedId}`),
          api.get(`api/TalentProfile/GetUserAffiliations/${resolvedId}`),
          api.get(`api/User/GetLocations`),
          api.get(`api/TalentProfile/GetUserVocalTypes/${resolvedId}`),
          api.get(`api/TalentProfile/GetUserMedia/${resolvedId}`),
          api.get(
            `api/TalentProfile/GetResumeSectionsWithDetails/${resolvedId}`,
          ),
          api.get(`api/TalentProfile/GetUserResumeTextData/${resolvedId}`),
          api.get(`api/User/get-all-representation-headings`),
          api.get(`api/User/get-vocal-ranges`),
        ]);

        const vocalRangeList = Array.isArray(vocalRangeRes?.data?.data)
          ? vocalRangeRes.data.data
          : Array.isArray(vocalRangeRes?.data)
            ? vocalRangeRes.data
            : [];

        const vocalTypeList = Array.isArray(vocalRes?.data)
          ? vocalRes.data
              .filter((v) => v.activeFlag)
              .map((v) => v.vocalTypeName)
          : [];

        setVocalRange(vocalRangeList);
        setVocalTypes(vocalTypeList);
        setTalentData(profileRes.data.data || {});
        setAffiliations(affRes.data || []);
        setLocations(locRes.data.data || []);

        const allMedia = mediaRes.data || [];
        setMedia(
          Array.isArray(allMedia)
            ? allMedia.filter((m) => m.activeFlag === 1)
            : [],
        );

        // Process resume text same way as TalentHome
        const textData = textRes.data || [];
        if (Array.isArray(textData)) {
          const processed = textData
            .filter((item) => item.activeFlag)
            .map((item) => ({
              ...item,
              resumeTextHeading: (item.resumeTextHeading || "")
                .toLowerCase()
                .split(" ")
                .filter(Boolean)
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" "),
            }))
            .reduce((acc, item) => {
              const existing = acc.find(
                (s) =>
                  (s.resumeTextHeading || "").toUpperCase() ===
                  (item.resumeTextHeading || "").toUpperCase(),
              );
              if (!existing) {
                acc.push(item);
              } else if (
                new Date(item.dateRecordCreated) >
                new Date(existing.dateRecordCreated)
              ) {
                acc[acc.indexOf(existing)] = item;
              }
              return acc;
            }, []);
          setResumeSections(processed);
        }

        setGroupedProjects(projRes.data?.data || projRes.data || []);

        const validHeadings = headingsRes.data?.data || headingsRes.data || [];
        const headingNames = validHeadings.map((h) => h.headingName);
        setHeadings(headingNames);

        const reps = {};
        await Promise.all(
          headingNames.map(async (headingName) => {
            try {
              const res = await api.get(
                `api/TalentProfile/GetRepresentationInformation/${resolvedId}/${headingName}`,
              );
              reps[headingName] = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [res.data];
            } catch {
              reps[headingName] = [];
            }
          }),
        );
        setRepresentationInfoList(reps);
      } catch (err) {
        console.error("Error loading talent data:", err);
        setNotFound(true);
      }
    };

    loadAll();
  }, [resolvedId]);

  // -------- Media processing (same as TalentHome) --------
  const sortedMedia = useMemo(() => {
    return [...(Array.isArray(media) ? media : [])].sort(
      (a, b) => (a.picSort || 0) - (b.picSort || 0),
    );
  }, [media]);

  const pictureItems = useMemo(
    () => sortedMedia.filter((m) => m.mediaType?.toLowerCase() === "picture"),
    [sortedMedia],
  );

  const videoItems = useMemo(
    () =>
      sortedMedia.filter((m) =>
        ["ytvideo", "vimvideo", "video"].includes(m.mediaType?.toLowerCase()),
      ),
    [sortedMedia],
  );

  const audioItems = useMemo(
    () => sortedMedia.filter((m) => m.mediaType?.toLowerCase() === "audio"),
    [sortedMedia],
  );

  const profilePic = useMemo(
    () =>
      sortedMedia.find((m) => m.mediaType?.toLowerCase() === "profile")
        ?.mediaName,
    [sortedMedia],
  );

  const hasAnyMedia =
    pictureItems.length > 0 || videoItems.length > 0 || audioItems.length > 0;
  const hasCredits =
    Array.isArray(groupedProjects) && groupedProjects.length > 0;
  const hasTextSections =
    Array.isArray(resumeSections) && resumeSections.length > 0;
  const showRightNav = hasAnyMedia || hasCredits || hasTextSections;

  const locationLabel =
    [talentData?.city, talentData?.location].filter(Boolean).join(", ") ||
    "Location";
  const contact = {
    phone: talentData.primaryContactNumber || "",
    secondaryPhone: talentData.secondaryContactNumber || "",
    email: talentData.email || "",
    faceBook: talentData.faceBook || "",
    twitter: talentData.twitter || "",
    instagram: talentData.instagram || "",
    tikTok: talentData.tikTok || "",
    personalWebSite: talentData.personalWebSite || "",
    paymentLink1: talentData.paymentLink1 || "",
    paymentLink2: talentData.paymentLink2 || "",
    paymentLink3: talentData.paymentLink3 || "",
    paymentLabel1: talentData.paymentLabel1 || "",
    paymentLabel2: talentData.paymentLabel2 || "",
    paymentLabel3: talentData.paymentLabel3 || "",
    imdb: talentData.imdb || "",
    youTube: talentData.youTube || "",
    snapChat: talentData.snapChat || "",
  };

  // -------- Location + Contact bar (read-only version) --------
  const LocationActionBar = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row" ,
        alignItems:"center",
        justifyContent:"space-between",
        gap: 1.5,
        mb: "12px",
        width: "100%",

      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PlaceIcon sx={{ color: "#e53935", fontSize: 20 }} />
        <Typography sx={{ fontWeight: 600 }}>{locationLabel}</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          marginLeft: { md: "auto" },
          width: { xs: "100%", md: "auto" },
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => setMsgOpen(true)}
          sx={{ cursor: "pointer", fontWeight: 500 }}
        >
          CONTACT
        </Button>

        {/*  <Button
          variant="contained"
          size="small"
          onClick={() => setMsgOpen(true)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          MESSAGE
        </Button> */}
      </Box>
    </Box>
  );

  // -------- Loading / Not found states --------
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <Typography variant="h5">Talent profile not found.</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          The username <b>{username}</b> does not exist or the profile is
          private.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <NavbarTalent />

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          py: { xs: 1, md: 2 },
          px: { xs: 1, md: 0 },
        }}
      >
        {/* Main layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {isMobile && <LocationActionBar />}

          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: "100%", md: "250px" },
              minWidth: { xs: "100%", md: "250px" },
              flexShrink: 0,
            }}
          >
            <TalentSidebar
              profilePic={profilePic}
              talentData={talentData}
              affiliations={affiliations}
              locations={locations}
              vocalTypes={vocalTypes}
              vocalRange={vocalRange}
              sidebarMediaEditMode={false}
              setEditPromptOpen={() => {}}
              setEditSection={() => {}}
              setEditInitialData={() => {}}
              representationInfo={representationInfoList}
              updateDocuments={() => {}}
            />
          </Box>

          {/* Main content */}
          {/* Main content */}
          <Box
            sx={{
              flexGrow: 1,
              marginTop: !isMobile && hasAnyMedia ? "0" : 0,
              width: "100%",
            }}
          >
            {!isMobile && <LocationActionBar />}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Media block */}
                {hasAnyMedia && (
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "5px",
                    }}
                  >
                    <Box id="pictures" sx={{ scrollMarginTop: "80px" }}>
                      {pictureItems.length > 0 && (
                        <MediaGallery
                          pictures={pictureItems}
                          onImageClick={(index) => {
                            setLightboxIndex(index);
                            setLightboxOpen(true);
                          }}
                        />
                      )}
                    </Box>

                    {videoItems.length > 0 && (
  <>
    {pictureItems.length > 0 && <Divider sx={{ my: 1 }} />}
    <Box id="video">
      <VideoReels videoItems={videoItems} />
    </Box>
  </>
)}

{audioItems.length > 0 && (
  <>
    {(pictureItems.length > 0 || videoItems.length > 0) && (
      <Divider sx={{ my: 1 }} />
    )}
    <Box id="audio">
      <AudioReels
        key={audioItems
          .map((a) => a.mediaId + "-" + a.picSort)
          .join("_")}
        audioItems={audioItems}
      />
    </Box>
  </>
)}
                  </Box>
                )}

                {/* Credits */}
                {hasCredits && (
                  <Box
                    id="credits"
                    sx={{
                      mt: 0.5,
                      backgroundColor: "#fff",
                      borderRadius: "5px",
                    }}
                  >
                    <ResumeProjectsSection
                      groupedResumeProjects={groupedProjects}
                      editMode={false}
                    />
                  </Box>
                )}

                {/* Text sections */}
                {hasTextSections && (
                  <Box id="textSections">
                    <ResumeTextSections
                      resumeSections={resumeSections}
                      resumeEditMode={false}
                      showAddNew={false}
                      showEmptySections={false}
                    />
                  </Box>
                )}
              </Box>

              {/* Sticky RightNav */}
              {showRightNav && !isMobile && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 50,
                    alignSelf: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  <RightNav />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={pictureItems.map((item) => ({ src: item.mediaName }))}
      />

      {/* Contact Dialog */}
      <ContactDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        contact={contact}
        headings={headings}
        representationInfoList={representationInfoList}
        editMode={false}
        showMessageButton={false}
      />

      {/* Message Prompt */}
      <MessagePrompt
        open={msgOpen}
        onClose={() => setMsgOpen(false)}
        onSent={() => setMsgOpen(false)}
        contact={contact}
        headings={headings}
        representationInfoList={representationInfoList}
        talentId={Number(resolvedId)}
        defaultSubject="Direct message"
        editorHeight={150}
        requireSenderEmail={!isLoggedIn}
        senderEmail={visitorEmail}
      />
    </>
  );
};

export default TalentPublicView;
